import React from "react";
import { cn } from "../../lib/utils";
import EmptyState from "./EmptyState";
import LoadingState from "./LoadingState";

const alignClasses = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
};

const getCellValue = (column, row, rowIndex) => {
  if (column.render) return column.render(row, rowIndex);
  if (column.accessor) return row?.[column.accessor];
  return row?.[column.id];
};

export function DataTable({
  columns,
  data = [],
  getRowKey = (row, rowIndex) => row?._id || row?.id || rowIndex,
  loading = false,
  loadingLabel,
  emptyIcon,
  emptyTitle,
  emptyDescription,
  className,
  tableClassName,
  mobile = true,
  mobileTitle,
  mobileSubtitle,
  mobileBadge,
  mobileActions,
  mobileCardClassName,
}) {
  if (loading) {
    return <LoadingState label={loadingLabel} />;
  }

  if (!data.length) {
    return (
      <EmptyState
        icon={emptyIcon}
        title={emptyTitle}
        description={emptyDescription}
      />
    );
  }

  const visibleMobileColumns = columns.filter(
    (column) => !column.hideOnMobile && !column.actions,
  );

  return (
    <>
      {mobile ? (
        <div className="space-y-3 md:hidden">
          {data.map((row, rowIndex) => (
            <div
              key={getRowKey(row, rowIndex)}
              className={cn("valhalla-card p-4", mobileCardClassName)}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  {mobileTitle ? (
                    <p className="break-words font-semibold text-[#F3F4F6]">
                      {mobileTitle(row, rowIndex)}
                    </p>
                  ) : null}
                  {mobileSubtitle ? (
                    <p className="mt-1 break-words text-sm text-[#9CA3AF]">
                      {mobileSubtitle(row, rowIndex)}
                    </p>
                  ) : null}
                </div>
                {mobileBadge ? (
                  <div className="shrink-0">{mobileBadge(row, rowIndex)}</div>
                ) : null}
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                {visibleMobileColumns.map((column) => {
                  const value = column.mobileRender
                    ? column.mobileRender(row, rowIndex)
                    : getCellValue(column, row, rowIndex);
                  if (value === undefined || value === null || value === "") {
                    return null;
                  }

                  return (
                    <div key={column.id} className="min-w-0">
                      <p className="text-xs uppercase tracking-wide text-[#9CA3AF]">
                        {column.mobileHeader || column.header}
                      </p>
                      <div
                        className={cn(
                          "mt-1 break-words text-[#F3F4F6]",
                          column.mobileClassName,
                        )}
                      >
                        {value}
                      </div>
                    </div>
                  );
                })}
              </div>

              {mobileActions ? (
                <div className="mt-4 flex flex-wrap justify-end gap-2 border-t border-[#242E42] pt-3">
                  {mobileActions(row, rowIndex)}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      <div
        className={cn(
          "valhalla-card overflow-hidden",
          mobile && "hidden md:block",
          className,
        )}
      >
        <table className={cn("data-table", tableClassName)}>
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.id}
                  className={cn(
                    alignClasses[column.align] || alignClasses.left,
                    column.headerClassName,
                  )}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, rowIndex) => (
              <tr key={getRowKey(row, rowIndex)}>
                {columns.map((column) => (
                  <td
                    key={column.id}
                    className={cn(
                      alignClasses[column.align] || alignClasses.left,
                      column.cellClassName,
                    )}
                  >
                    {getCellValue(column, row, rowIndex)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default DataTable;
